// Core
import React from 'react';

// Libraries
import _ from 'lodash';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  isEquipmentRunning,
  overallStatusToTagType,
  overallStatusToText,
} from '../../../../../../plugins/crusher';
import { convertPressure } from '../../../../../../plugins/general';
import {
  CLAMP_PRESSURE_RANGE,
  TRAMP_PRESSURE_RANGE,
} from '../../../../../../plugins/thresholds';

// Hook
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Types
import {
  MeasurementType,
  PressureType,
} from '../../../../../../types/global';
import { Theme } from '../../../../../../types/theme';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import { ModuleHeader } from '../../../../../../layout/elements/ModuleHeader';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import { Speedometer } from '../../../../../../layout/elements/Speedometer';
import { Tag } from '../../../../../../layout/elements/Tag';

// View
import { HydraulicProps } from './Hydraulic.types';
import {
  ClampPressureContainer,
  HydraulicContainer,
  OverallStatusContainer,
  SpeedometersContainer,
  TankTempStatusContainer,
  TrampPressureContainer,
} from './Hydraulic.style';

function Hydraulic({
  className,
  onOpenDrawer,
  unitData,
  edgeStatus,
  testId,
}: HydraulicProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs, isSm } = useBreakpoint();
  const isMobile: boolean = isXs || isSm;

  const pressureUnit: PressureType = getMeasurementUnit(MeasurementType.pressure);

  return (
    <HydraulicContainer
      data-testid={testId}
      className={className}
    >
      <Row>
        <Col>
          <ModuleHeader
            onClick={onOpenDrawer}
            title={t('view.crusher.hydraulic.title')}
            testId={`${testId}-module-header`}
          />
        </Col>
      </Row>
      <Row>
        <Col cols={12}>
          <OverallStatusContainer data-testid="overall-status-container">
            <Paragraph
              testId="overall-status-paragraph"
              size={ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.statusTitleColor}
              preventOverflow
            >
              {t('view.crusher.overallStatus')}
            </Paragraph>
            <Tag
              text={overallStatusToText(unitData?.hydraulicOverallStatus)}
              type={overallStatusToTagType(
                unitData?.hydraulicOverallStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
            />
          </OverallStatusContainer>
        </Col>
        <Col cols={12}>
          <TankTempStatusContainer data-testid="tank-temp-status-container">
            <Paragraph
              testId="tank-temp-paragraph"
              size={ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.statusTitleColor}
              preventOverflow
            >
              {t(`view.crusher.hydraulic.${isMobile ? 'tankTempMobile' : 'tankTemp'}`)}
            </Paragraph>
            <Tag
              text={overallStatusToText(unitData?.hydraulicTankTempStatus)}
              type={overallStatusToTagType(
                unitData?.hydraulicTankTempStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
            />
          </TankTempStatusContainer>
        </Col>
      </Row>
      <Row style={{ flex: 1 }}>
        <SpeedometersContainer>
          <ClampPressureContainer data-testid="clamp-pressure-container">
            <Col cols={12}>
              <Speedometer
                testId="clamp-pressure-speedometer"
                minValue={_.isNil(unitData?.hydraulicClampPressure)
                  ? null : CLAMP_PRESSURE_RANGE(pressureUnit).minValue}
                maxValue={_.isNil(unitData?.hydraulicClampPressure)
                  ? null : CLAMP_PRESSURE_RANGE(pressureUnit).maxValue}
                value={
                  convertPressure(
                    unitData?.hydraulicClampPressure,
                    pressureUnit,
                    2,
                  ).value
                }
                unit={pressureUnit}
                thresholds={{
                  lowAlarm: convertPressure(
                    unitData?.spClampPressureLowAlarm,
                    pressureUnit,
                    2,
                  ).value,
                }}
                title={`${t('view.crusher.hydraulic.clampPressure')}`}
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
              />
            </Col>
          </ClampPressureContainer>

          <TrampPressureContainer data-testid="tramp-pressure-container">
            <Col cols={12}>
              <Speedometer
                testId="tramp-pressure-speedometer"
                minValue={_.isNil(unitData?.hydraulicTrampPressure)
                  ? null : TRAMP_PRESSURE_RANGE(pressureUnit).minValue}
                maxValue={_.isNil(unitData?.hydraulicTrampPressure)
                  ? null : TRAMP_PRESSURE_RANGE(pressureUnit).maxValue}
                value={
                  convertPressure(
                    unitData?.hydraulicTrampPressure,
                    pressureUnit,
                    2,
                  ).value
                }
                unit={pressureUnit}
                thresholds={{
                  lowAlarm: convertPressure(
                    unitData?.spTrampPressureLowAlarm,
                    pressureUnit,
                    2,
                  ).value,
                }}
                title={`${t('view.crusher.hydraulic.trampPressure')}`}
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
              />
            </Col>
          </TrampPressureContainer>
        </SpeedometersContainer>
      </Row>
    </HydraulicContainer>
  );
}

export { Hydraulic };
